<template>
    <b-popover
        id="sortTreatmentPopOver"
        ref="sortPopover"
        :target="target"
        placement="bottomleft"
        triggers="click blur"
        custom-class="sort-treatment-board"
        class="pl-0 pr-0"
    >
        <!-- eslint-disable-next-line -->
        <template #title
            ><span class="text-title">Sort Treatment Board</span></template
        >
        <div class="pb-2">
            <ul class="list-group">
                <li
                    v-for="(item, index) in items"
                    :key="index"
                    class="list-group-item d-flex align-items-center"
                    href="#"
                    :class="{ 'disabled-item': disableButton }"
                    @click="setActive(item)"
                >
                    <!-- Always render the span but conditionally display the tick -->
                    <span class="check-mark" v-show="activeItem === item"
                        >✔</span
                    >
                    <!-- Spacer element to maintain the gap even if tick is not visible -->
                    <span
                        class="check-mark-placeholder"
                        aria-hidden="true"
                        v-show="activeItem !== item"
                        >✔</span
                    >
                    <span class="item-text">{{ item.label }}</span>
                </li>
            </ul>
        </div>
    </b-popover>
</template>
<script>
// helper
import { orderBy, find, isEmpty, maxBy, minBy, cloneDeep } from "lodash";
export default {
    props: {
        target: {
            type: String
        },
        treatments: {
            type: Array,
            required: true
        },
        activityStatus: {
            type: Object
        }
    },
    data() {
        return {
            items: [
                { id: 1, label: "Patient Name: A to Z" },
                { id: 2, label: "Patient Name: Z to A" },
                { id: 3, label: "Triage Status: High to Low" },
                { id: 4, label: "Triage Status: Low to High" },
                { id: 5, label: "Treatment Start Time: Oldest First" },
                { id: 6, label: "Treatment Start Time: Most Recent" },
                { id: 7, label: "Treatment Due: Activity Due First" },
                { id: 8, label: "Treatment Due: Activity Due Last" }
            ],
            activeItem: { id: null, label: null },
            disableButton: false
        };
    },
    async mounted() {
        this.disableButton = true;
        await this.$nextTick();
        if (!isEmpty(this.$store.state.userPreferences.prefs)) {
            // re-fetch soap pref to cater case user navigate away
            await this.$store.dispatch("userPreferences/initGetPrefs");
        } else {
            // Ensure the selected item is actually from the items array to maintain reference equality
            this.activeItem = find(this.items, item => item.id === 1);
        }
        // Add click listener to detect clicks outside the popover
        document.addEventListener("click", this.handleClickOutside);
    },
    beforeDestroy() {
        // Remove the click listener when the component is destroyed
        document.removeEventListener("click", this.handleClickOutside);
    },
    computed: {
        preparedTreatments() {
            let preparedTreatments = cloneDeep(this.treatments).map(
                treatment => {
                    if (
                        treatment.activities &&
                        treatment.activities.length > 0
                    ) {
                        let prepareActivities = treatment.activities
                            .filter(
                                activity =>
                                    activity.status ==
                                    this.activityStatus.PENDING
                            )
                            .map(activity => {
                                activity.useDue = activity.due;
                                if (activity.due_type == 1) {
                                    activity.useDue =
                                        this._fromDateCarbon(
                                            treatment.create_at
                                        ).toSeconds() + activity.due;
                                }
                                return activity;
                            });
                        treatment.furthestDue = maxBy(
                            prepareActivities,
                            "useDue"
                        )?.useDue;
                        treatment.nearestDue = minBy(
                            prepareActivities,
                            "useDue"
                        )?.useDue;
                    }
                    return treatment;
                }
            );
            return preparedTreatments;
        },
        sortTreatmentBoard() {
            let sortedTreatmentBoard = this.preparedTreatments;

            let isIncreasingOrder = true;
            switch (this.activeItem?.id) {
                case 1:
                    sortedTreatmentBoard = orderBy(
                        sortedTreatmentBoard,
                        [
                            "patient.name",
                            treatment =>
                                this._fromDateCarbon(
                                    treatment.create_at
                                ).toSeconds()
                        ],
                        ["asc", "asc"]
                    );
                    break;
                case 2:
                    sortedTreatmentBoard = orderBy(
                        sortedTreatmentBoard,
                        [
                            "patient.name",
                            treatment =>
                                this._fromDateCarbon(
                                    treatment.create_at
                                ).toSeconds()
                        ],
                        ["desc", "desc"]
                    );
                    isIncreasingOrder = false;
                    break;
                case 3:
                    sortedTreatmentBoard = orderBy(
                        sortedTreatmentBoard,
                        [
                            treatment =>
                                find(treatment.active_triage, {
                                    isactive: true
                                }),
                            "active_triage.triage_type_id",
                            treatment =>
                                this._fromDateCarbon(
                                    treatment.create_at
                                ).toSeconds()
                        ],
                        ["asc", "asc", "desc"]
                    );
                    isIncreasingOrder = false;
                    break;
                case 4:
                    sortedTreatmentBoard = orderBy(
                        sortedTreatmentBoard,
                        [
                            treatment =>
                                find(treatment.active_triage, {
                                    isactive: true
                                }),
                            "active_triage.triage_type_id",
                            treatment =>
                                this._fromDateCarbon(
                                    treatment.create_at
                                ).toSeconds()
                        ],
                        ["asc", "desc", "asc"]
                    );
                    break;
                case 5:
                    sortedTreatmentBoard = orderBy(
                        sortedTreatmentBoard,
                        [
                            treatment =>
                                find(treatment.treatment_status, {
                                    isactive: true
                                }).status_id,
                            treatment =>
                                this._fromDateCarbon(
                                    treatment.create_at
                                ).toSeconds()
                        ],
                        ["desc", "asc"]
                    );
                    isIncreasingOrder = false;
                    break;
                case 6:
                    sortedTreatmentBoard = orderBy(
                        sortedTreatmentBoard,
                        [
                            treatment =>
                                find(treatment.treatment_status, {
                                    isactive: true
                                }).status_id,
                            treatment =>
                                this._fromDateCarbon(
                                    treatment.create_at
                                ).toSeconds()
                        ],
                        ["desc", "desc"]
                    );
                    break;
                case 7:
                    sortedTreatmentBoard = orderBy(
                        [...sortedTreatmentBoard], // Ensure a new array
                        [
                            treatment => (treatment.nearestDue ? 0 : 1),
                            treatment => treatment.nearestDue,
                            treatment =>
                                find(treatment.treatment_status, {
                                    isactive: true
                                }).status_id
                        ],
                        ["asc", "asc", "desc"]
                    );
                    break;
                case 8:
                    sortedTreatmentBoard = orderBy(
                        [...sortedTreatmentBoard], // Ensure a new array
                        [
                            treatment => (treatment.furthestDue ? 0 : 1),
                            treatment => treatment.furthestDue,
                            treatment =>
                                find(treatment.treatment_status, {
                                    isactive: true
                                }).status_id
                        ],
                        ["asc", "desc", "desc"]
                    );
                    isIncreasingOrder = false;
                    break;
            }
            this.$emit("change-icon-down", isIncreasingOrder);
            return sortedTreatmentBoard;
        }
    },
    watch: {
        sortTreatmentBoard(value) {
            this.$emit("refresh", value);
        },
        "$store.state.userPreferences.prefs"(value) {
            let userPref = value?.treatmentBoardSort?.sort?.id || 1;

            // Ensure the selected item is actually from the items array to maintain reference equality
            this.activeItem = find(this.items, item => item.id === userPref);
            this.disableButton = false;
        },
        disableButton(val) {
            this.$emit("disabled-button", val);
        }
    },
    methods: {
        setActive(item) {
            this.activeItem = item;
            this.saveSortTreatmentBoardPreferences(item);
        },
        async saveSortTreatmentBoardPreferences(value) {
            this.disableButton = true;
            await this.$store
                .dispatch("userPreferences/updatePref", {
                    category: "treatmentBoardSort",
                    data: { sort: value }
                })
                .finally(() => {
                    this.disableButton = false;
                });
        },
        async handleClickOutside(event) {
            // Wait until the DOM updates and popover is fully rendered
            await this.$nextTick();
            const popoverEl = document.getElementById("sortTreatmentPopOver");

            // Check if targetEl and popoverEl exist and then apply the contains logic
            if (popoverEl && !popoverEl.contains(event.target)) {
                this.$refs.sortPopover.$emit("close");
            }
        }
    }
};
</script>
<style scoped>
.sort-treatment-board {
    width: auto; /* Automatically adjust the width based on content */
    max-width: none; /* Remove any maximum width constraints */
    white-space: nowrap; /* Prevents text wrapping inside the popover */
}

.text-title {
    font-size: 14px;
}

.list-group-item {
    cursor: pointer;
    border: none !important;
    display: flex;
    align-items: center; /* Aligns the text vertically in the middle */
    padding: 5px 10px; /* Adds padding to make the items look more balanced */
    white-space: nowrap; /* Prevents text wrapping */
    transition: background-color 0.3s ease; /* Smooth transition for background color */
}

.list-group-item:hover {
    background-color: #c9ccde; /* Light gray background on hover */
    border-radius: 4px; /* Optional: adds rounded corners on hover */
}

.check-mark,
.check-mark-placeholder {
    width: 20px; /* Fixed width for tick mark */
    text-align: center; /* Center align the tick within its column */
    margin-right: 1rem !important;
}

.check-mark-placeholder {
    visibility: hidden; /* Hide the placeholder */
}

.item-text {
    flex-grow: 1; /* Allows the text to take up remaining space */
    padding-right: 10px; /* Adds some padding on the right */
    text-align: left; /* Ensures text is aligned to the left */
    white-space: nowrap; /* Prevents text wrapping */
    overflow: visible; /* Ensures text is fully visible */
}

.disabled-item {
    color: #6c757d; /* Bootstrap's $gray-600 */
    pointer-events: none; /* Prevents clicking */
    cursor: default;
}
</style>
